import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Controller,
  useFieldArray,
  useWatch,
  type UseFormReturn,
} from 'react-hook-form';

import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Typography,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Menu,
  MenuItem,
  Skeleton,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhoneIcon from '@mui/icons-material/Phone';

import { TripLegStatus } from '../../../../data/models/trip-leg-status.model';
import type {
  SyncedTripLeg,
  TripLeg,
} from '../../../../data/models/reservation.model';
import type { Provider } from '../../../../data/models/provider.model';
import { ProviderCallOutcomeBC } from '../../../../data/models/provider-call-outcome.model';

import {
  selectCallOutcomes,
  selectCallOutcomeRejectReasons,
  selectTransportationProviderPhoneUses,
} from '../../../../data/lookup.slice';

import { useAppSelector } from '../../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';

import { phoneUtils } from '../../../../services/phoneUtils';

import { ProviderCallSummary } from './ProviderCallSummary';
import Checkbox from '../../../forms/Checkbox';

const PREFIX = 'ProviderCallForm';
const classes = {
  container: `${PREFIX}-container`,
  form: `${PREFIX}-form`,
  details: `${PREFIX}-details`,
  actions: `${PREFIX}-actions`,
  provider: `${PREFIX}-provider`,
  providerName: `${PREFIX}-providerName`,
  providerContact: `${PREFIX}-providerContact`,
  providerPhone: `${PREFIX}-providerPhone`,
  fieldset: `${PREFIX}-fieldset`,
  fieldLabel: `${PREFIX}-field-label`,
  fieldSelect: `${PREFIX}-fieldSelect`,
  radioGroup: `${PREFIX}-radioGroup`,
  radioSubGroup: `${PREFIX}-radioSubGroup`,
  currentTripLeg: `${PREFIX}-currentTripLeg`,
  reducedWidth: `${PREFIX}-reducedWidth`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'flex-start',
    gap: theme.spacing(4),
    flexWrap: 'wrap',
    placeItems: 'flex-start',
  },
  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(4),
    gap: theme.spacing(4),
    minWidth: 600,
  },
  [`& .${classes.actions}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
    ['& .MuiButton-root']: {
      width: '144px',
    },
  },

  [`& .${classes.providerName}`]: {
    marginBottom: theme.spacing(0.5),
    paddingBottom: 0,
  },
  [`& .${classes.providerContact}`]: {
    paddingBottom: 0,
  },
  [`& .${classes.providerPhone}`]: {
    color: theme.palette.lightTextSecondary.main,
    paddingBottom: 0,
    display: 'flex',
    alignItems: 'flex-end',
    gap: 4,
    '& .MuiSvgIcon-root:first-of-type': {
      marginLeft: 4,
    },
  },

  [`& .${classes.fieldLabel}`]: {
    fontWeight: '600',
    marginBottom: theme.spacing(1),
    paddingBottom: 0,
  },
  [`& .${classes.fieldSelect}`]: {
    marginLeft: theme.spacing(4),
  },
  [`& .${classes.radioGroup}`]: {
    display: 'flex',
    marginBottom: theme.spacing(4),
    flexDirection: 'column',

    '& .MuiFormControlLabel-root': {
      marginLeft: 0,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',

      '& .MuiTypography-root': {
        paddingBottom: 0,
      },
    },
  },
  [`& .${classes.radioSubGroup}`]: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: theme.spacing(5),
  },
  [`& .${classes.currentTripLeg}`]: {
    color: theme.palette.error.main,
  },
}));

const StyledMenuButton = styled(Box)(({ theme }) => ({
  width: '337px',
  height: '100%',
  padding: theme.spacing(1.5),
  marginLeft: theme.spacing(5),
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  background: '#FFFFFF',
  borderRadius: '3px',
  boxShadow: '0px 2px 7px 0px #01101226',
  color: 'theme.palette.lightTextPrimary.main',
  transition: 'width 0.2s ease-in-out',
  fontFamily: theme.typography.subtitle1.fontFamily,
  fontSize: theme.typography.subtitle1.fontSize,
  fontWeight: theme.typography.subtitle1.fontWeight,
  lineHeight: theme.typography.body1.lineHeight,
  letterSpacing: theme.typography.subtitle1.letterSpacing,

  [`&.${classes.reducedWidth}`]: {
    width: '137px',
  },

  [`& .MuiSvgIcon-root`]: {
    marginLeft: 'auto',
  },
}));

const StyledDropdownGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  gap: '10px',
});

const StyledMenuList = styled(Menu)({
  ['& .MuiPaper-root']: {
    minWidth: '220px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    boxShadow: '0px 2px 7px 0px #01101226',
    color: 'rgba(0, 0, 0, 0.6)',
  },
  ['& .MuiList-root']: {
    width: '100%',
  },
});

const StyledInputContainer = styled('div')({
  flexGrow: 1,
  height: '48px',
  padding: '12px',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  background: '#FFFFFF',
  borderRadius: '3px',
  boxShadow: '0px 2px 7px 0px #01101226',
  color: '#0000000F',
});

const StyledInput = styled('input')({
  width: '100%',
  border: 'none',
  outline: 'none',
  color: '#01101299',
  fontFamily: 'Source Sans Pro',
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px',
  letterSpacing: '0.15px',
  textAlign: 'left',
  textUnderlinePosition: 'from-font',
  textDecorationSkipInk: 'none',
});

const rejectedReasonSetValueOptions = {
  shouldDirty: true,
  shouldTouch: true,
  shouldValidate: true,
};

interface TripLegsInReservationProps {
  tripLegsInReservation: TripLeg[];
  tripLeg: TripLeg;
  formHook: UseFormReturn<any, any>;
}

const TripLegsInReservation = ({
  tripLegsInReservation,
  tripLeg,
  formHook,
}: TripLegsInReservationProps) => {
  const { t } = useTranslation('common');

  return (
    <>
      {tripLegsInReservation.map((tripLegInReservation, idx) => {
        const classNames = [classes.radioSubGroup];
        const currentTripLeg =
          tripLegInReservation.tripLegReferenceID ===
          tripLeg.tripLegReferenceID;
        if (currentTripLeg) {
          classNames.push(classes.currentTripLeg);
        }
        const label = `${t('findProvider.tripLeg')} ${idx + 1} (${currentTripLeg ? 'Current - ' : ''}${tripLegInReservation.tripLegReferenceID})`;
        return (
          <Box
            className={classNames.join(' ')}
            key={`trip-leg-${tripLegInReservation.tripLegReferenceID}`}>
            <Controller
              name={`tripLegIDs[${idx}].value`}
              control={formHook.control}
              key={`trip-leg-controller-${tripLegInReservation.tripLegReferenceID}`}
              rules={{
                validate: (value) => {
                  if (
                    formHook.getValues('callOutcomeBC') ===
                    ProviderCallOutcomeBC.Accepted
                  ) {
                    const anyChecked = tripLegsInReservation.some(
                      (_leg, idx) => {
                        return formHook.getValues(`tripLegIDs[${idx}].value`);
                      },
                    );
                    return (
                      anyChecked || 'At least one trip leg must be selected'
                    );
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <Checkbox {...field} control={formHook.control} label={label} />
              )}
            />
          </Box>
        );
      })}
    </>
  );
};

interface RejectedReasonDropdownProps {
  open: boolean;
  onClose: (evt: React.MouseEvent<HTMLElement>) => void;
  anchorEl: HTMLElement | null;
  selectedReasonID: number;
  handleToggle: (evt: React.MouseEvent<HTMLElement>) => void;
  handleSelect: (evt: any, id: number) => void;
  handleChange: (evt: any) => void;
}

const RejectedReasonDropdown = ({
  open,
  onClose,
  anchorEl,
  selectedReasonID,
  handleToggle,
  handleSelect,
  handleChange,
}: RejectedReasonDropdownProps) => {
  const { t } = useTranslation('common');

  const rejectedReasonOptions = useAppSelector(selectCallOutcomeRejectReasons);
  const reasonMap = new Map(
    rejectedReasonOptions.map((reason) => [
      reason.callOutcomeRejectReasonID,
      {
        bc: reason.callOutcomeRejectReasonBC,
        name: reason.callOutcomeRejectReasonName,
      },
    ]),
  );
  const selReason = reasonMap.get(selectedReasonID);
  const isOtherSelected = selReason?.bc === 'OTHER';

  return (
    <StyledDropdownGroup>
      <StyledMenuButton
        className={isOtherSelected ? classes.reducedWidth : ''}
        onClick={handleToggle}>
        {Boolean(selectedReasonID) && reasonMap.get(selectedReasonID)?.name}
        {!selectedReasonID && t('findProvider.reason')}
        {open && <ExpandLessIcon sx={{ color: '#0110128A' }} />}
        {!open && <ExpandMoreIcon sx={{ color: '#0110128A' }} />}
      </StyledMenuButton>
      <StyledMenuList open={open} onClose={onClose} anchorEl={anchorEl}>
        {rejectedReasonOptions.map((option, idx) => {
          return (
            <MenuItem
              key={`${option.callOutcomeRejectReasonID}`}
              onClick={(evt: React.MouseEvent<HTMLLIElement>) =>
                handleSelect(evt, option.callOutcomeRejectReasonID)
              }>
              {option.callOutcomeRejectReasonName}
            </MenuItem>
          );
        })}
      </StyledMenuList>
      {isOtherSelected && (
        <StyledInputContainer>
          <StyledInput
            placeholder={t('findProvider.rejectedReasonOther')}
            type="text"
            maxLength={50}
            onChange={handleChange}
          />
        </StyledInputContainer>
      )}
    </StyledDropdownGroup>
  );
};

interface ProviderCallFormProps {
  formHook: UseFormReturn<any, any>;
  isSaving: boolean;
  isLoading: boolean;
  isAllocationsValid: boolean;
  tripLeg: SyncedTripLeg;
  tripLegsInReservation: TripLeg[];
  provider: Provider | null;
  submit: () => void;
  close: () => void;
  currentAttempt: number;
  maxAttempts: number;
}

function ProviderCallForm({
  formHook,
  isSaving,
  isLoading,
  isAllocationsValid,
  tripLeg,
  tripLegsInReservation,
  provider,
  submit,
  close,
  currentAttempt,
  maxAttempts,
}: ProviderCallFormProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const [rejectReasonSelectMenuAnchorElm, setRejectReasonSelectMenuAnchorElm] =
    useState<HTMLElement | null>(null);
  const [rejectedReasonID, setRejectedReasonID] = useState(0);
  const rejectReasonMenuOpen = Boolean(rejectReasonSelectMenuAnchorElm);

  const providerPhoneUses = useAppSelector(
    selectTransportationProviderPhoneUses,
  );

  const callOutcomes = useAppSelector(selectCallOutcomes);
  const rejectedReasonOptions = useAppSelector(selectCallOutcomeRejectReasons);

  const { fields: tripLegIDFields, replace } = useFieldArray({
    control: formHook.control,
    name: 'tripLegIDs',
  });
  // const watchTripLegIDs = formHook.watch('tripLegIDs');
  const watchCallOutcome = formHook.watch('callOutcomeBC');
  const watchServiceAssurance = formHook.watch('serviceAssurance');
  const watchRejectedReason = formHook.watch('rejectedReason');
  const watchRejectedReasonOther = formHook.watch('rejectedReasonOther');
  const watchTripLegIDs = useWatch({
    control: formHook.control,
    name: 'tripLegIDs',
  });
  const selectedRejectedReason = rejectedReasonOptions.find(
    (reason) =>
      reason.callOutcomeRejectReasonID === Number(watchRejectedReason),
  );
  const isOtherRejectedReasonSelected =
    selectedRejectedReason?.callOutcomeRejectReasonBC === 'OTHER';

  const hasTripLegSelected = watchTripLegIDs.some(
    (field: { value: boolean }) => field.value,
  );

  const isFormValid = formHook.formState.isValid;
  const isFormDirty = formHook.formState.isDirty;
  const isRejectedReasonMissing =
    watchCallOutcome === ProviderCallOutcomeBC.Rejected && !watchRejectedReason;
  const isRejectedReasonOtherMissing =
    isOtherRejectedReasonSelected && !watchRejectedReasonOther;

  const isSubmitDisabled =
    isLoading ||
    isSaving ||
    !isFormValid ||
    !isFormDirty ||
    isRejectedReasonMissing ||
    isRejectedReasonOtherMissing ||
    !isAllocationsValid;

  // Check for dispatch in phones, fallback to first phone number
  const providerName = provider?.transportationProviderName;
  const providerDispatch = phoneUtils.getProviderDispatchPhone(
    provider?.transportationProviderPhones,
    providerPhoneUses,
  );
  const providerContactName = providerDispatch
    ? `${providerDispatch?.contactFirstName} ${providerDispatch?.contactLastName}`
    : '';
  const providerDispatchPhone = providerDispatch?.phoneNumber;
  const providerContactPhone = providerDispatchPhone
    ? phoneUtils.formatPhoneString(providerDispatchPhone, false, false)
    : '';

  const handleClose = () => {
    close();
  };

  const handleRejectedReasonToggle = (evt: React.MouseEvent<HTMLElement>) => {
    formHook.setValue(
      'callOutcomeBC',
      ProviderCallOutcomeBC.Rejected,
      rejectedReasonSetValueOptions,
    );
    setRejectReasonSelectMenuAnchorElm(
      rejectReasonSelectMenuAnchorElm ? null : evt.currentTarget,
    );
  };

  const setTripLegIDCheckboxes = (
    tripLegIDFields: Record<'id', string>[],
    value: boolean,
  ) => {
    tripLegIDFields.map((_field, idx) => {
      formHook.setValue(`tripLegIDs[${idx}].value`, value);
    });
  };

  const clearRejectedReason = () => {
    formHook.setValue('rejectedReason', '');
    formHook.setValue('rejectedReasonOther', '');
    setRejectedReasonID(0);
  };

  const handleRejectedReasonSelect = (evt: any, id: number) => {
    formHook.setValue(
      'callOutcomeBC',
      ProviderCallOutcomeBC.Rejected,
      rejectedReasonSetValueOptions,
    );
    formHook.setValue('rejectedReason', id, rejectedReasonSetValueOptions);
    setRejectedReasonID(id);
    setRejectReasonSelectMenuAnchorElm(null);
  };

  const handleRejectedReasonOtherChange = (evt: any) => {
    formHook.setValue(
      'rejectedReasonOther',
      evt.target.value,
      rejectedReasonSetValueOptions,
    );
  };

  // const handleServiceAssuranceChange = (evt: any) => {
  //   formHook.setValue('serviceAssurance', evt.target.value);
  // };

  useEffect(() => {
    if (watchRejectedReasonOther) {
      formHook.trigger('rejectedReasonOther');
    }
    if (watchServiceAssurance) {
      formHook.trigger('serviceAssurance');
    }
  }, [watchRejectedReasonOther, watchServiceAssurance]);

  // Add validation and interaction rules
  useEffect(() => {
    if (watchCallOutcome !== ProviderCallOutcomeBC.Accepted) {
      setTripLegIDCheckboxes(watchTripLegIDs, false);
    }

    if (
      watchCallOutcome === ProviderCallOutcomeBC.Accepted &&
      !hasTripLegSelected
    ) {
      setTripLegIDCheckboxes(watchTripLegIDs, true);
    }

    if (watchCallOutcome !== ProviderCallOutcomeBC.Rejected) {
      clearRejectedReason();
    }
    if (watchCallOutcome === ProviderCallOutcomeBC.Rejected) {
      formHook.register('rejectedReason', {
        required: t('findProvider.rejectedReasonRequired'),
      });
      formHook.trigger('rejectedReason');
    } else {
      formHook.unregister('rejectedReason');
    }
  }, [watchCallOutcome]);

  useEffect(() => {
    if (
      watchCallOutcome !== ProviderCallOutcomeBC.Accepted &&
      hasTripLegSelected
    ) {
      formHook.setValue('callOutcomeBC', ProviderCallOutcomeBC.Accepted);
    }
  }, [hasTripLegSelected]);

  useEffect(() => {
    if (isOtherRejectedReasonSelected) {
      formHook.register('rejectedReasonOther', {
        required: t('findProvider.rejectedReasonRequired'),
      });
      formHook.trigger('rejectedReasonOther');
    } else {
      formHook.unregister('rejectedReasonOther');
    }
  }, [isOtherRejectedReasonSelected]);

  // Filter out any trip legs that are not open
  const tripLegsFiltered = tripLegsInReservation.filter(
    (tripLegInReservation) =>
      tripLegInReservation.status === TripLegStatus.Open,
  );

  // If at some point between selecting the trip leg and selecting
  // a provider, the trip leg status may change. If all trip legs
  // are no longer open, close the dialog and notify the user
  // if (!isLoading && tripLegsFiltered.length === 0) {
  //   dispatch(setErrorMessage(t('findProvider.selectedTripLegNotOpen')));
  //   handleClose();
  // }

  return (
    <StyledBox className={classes.container}>
      <Paper elevation={2} className={classes.form}>
        <Box className={classes.provider}>
          {isLoading && (
            <>
              <Skeleton variant="text" width={300} height={36} />
              <Skeleton variant="text" width={150} height={24} />
              <Skeleton variant="text" width={200} height={24} />
            </>
          )}
          {!isLoading && (
            <>
              <Typography variant="h3" className={classes.providerName}>
                {providerName || ''}
              </Typography>
              {providerContactName && (
                <>
                  <Typography
                    variant="body1"
                    className={classes.providerContact}>
                    {providerContactName ?? ''}
                  </Typography>
                  <Typography variant="body1" className={classes.providerPhone}>
                    {providerContactPhone ?? ''}
                    <ChatBubbleOutlineIcon fontSize="small" />
                    <PhoneIcon fontSize="small" />
                  </Typography>
                </>
              )}
            </>
          )}
        </Box>

        <Box className={classes.fieldset}>
          <Typography className={classes.fieldLabel} variant="body1">
            {t('findProvider.whatWasTheOutcome')}
          </Typography>
          <FormControl>
            <Controller
              name="callOutcomeBC"
              control={formHook.control}
              render={({ field }) => (
                <RadioGroup className={classes.radioGroup} {...field}>
                  {callOutcomes.map((callOutcome) => {
                    return (
                      <>
                        <FormControlLabel
                          key={callOutcome.callOutcomeBC}
                          value={callOutcome.callOutcomeBC}
                          control={<Radio />}
                          label={callOutcome.callOutcomeName}
                        />
                        {callOutcome.callOutcomeBC ===
                          ProviderCallOutcomeBC.Rejected && (
                          <RejectedReasonDropdown
                            open={rejectReasonMenuOpen}
                            onClose={handleRejectedReasonToggle}
                            anchorEl={rejectReasonSelectMenuAnchorElm}
                            selectedReasonID={rejectedReasonID}
                            handleToggle={handleRejectedReasonToggle}
                            handleSelect={handleRejectedReasonSelect}
                            handleChange={handleRejectedReasonOtherChange}
                          />
                        )}
                        {callOutcome.callOutcomeBC ===
                          ProviderCallOutcomeBC.Accepted && (
                          <TripLegsInReservation
                            tripLegsInReservation={tripLegsFiltered}
                            tripLeg={tripLeg as unknown as TripLeg}
                            formHook={formHook}
                          />
                        )}
                      </>
                    );
                  })}
                </RadioGroup>
              )}
            />

            {/* <StyledInputContainer>
              <StyledInput
                placeholder={t('findProvider.serviceAssurance')}
                type="text"
                onChange={handleServiceAssuranceChange}
              />
            </StyledInputContainer> */}
          </FormControl>
        </Box>

        <Box className={classes.actions}>
          <Button
            color="primary"
            variant="contained"
            disabled={isSubmitDisabled}
            size="large"
            onClick={submit}>
            {currentAttempt >= maxAttempts
              ? t('common.submit')
              : t('common.next')}
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={handleClose}
            size="large">
            {t('common.close')}
          </Button>
        </Box>
      </Paper>
      <ProviderCallSummary
        tripLeg={tripLeg}
        tripLegsInReservation={tripLegsFiltered}
        providerContactName={providerContactName ?? ''}
        isLoading={isLoading}
      />
    </StyledBox>
  );
}

export { ProviderCallForm };
